import React from 'react';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useContentId } from '@confluence/page-restrictions-context/entry-points/useContentId.ts';
import { usePageRestrictionsContext } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { IsUpsellQueryComponentProps } from '../types.ts';

export function IsUpsellQueryComponent(props: IsUpsellQueryComponentProps) {
	const contentId = useContentId(props.contentId);
	const { restrictions, error, loading } = usePageRestrictionsContext();
	const { edition } = useSessionData();

	if (contentId) {
		if (loading) {
			return null;
		}

		if (error) {
			// For legacy purposes, we don't render `children` when there is an
			// `error`.
			if (isUnauthorizedError(error)) {
				// `edition` (e.g. anonymous access is not permitted) and/or
				// `content` is not permitted to the current user and in either case
				// we know we don't want to do an upsell related to Restrictions
				// dialog. So here an unauthorized error is an expected error.
				// The error is marked as expected inside PageRestrictionsContextProvider.
				return null;
			} else {
				return <ErrorDisplay error={error} />;
			}
		}

		const hasRestrictions = Boolean(restrictions?.hasRestrictions);
		const hasInheritedRestrictions = Boolean(restrictions?.hasInheritedRestrictions);
		const isContentRestricted = hasRestrictions || hasInheritedRestrictions;

		return props.children({
			isUpsell: edition === 'FREE' && !isContentRestricted,
		});
	} else {
		return props.children({
			isUpsell: false,
		});
	}
}
